import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Navigation, Pagination, Autoplay, Keyboard } from "swiper/modules";
import { ArrowUpRight } from "lucide-react";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { SITE } from "@/constants/testIds";
import { PROJECTS } from "@/data/projects";

function ProjectCard({ project, index }) {
  return (
    <Link
      to={`/project/${project.id}`}
      className="tb-project-card block group"
      data-testid={`${SITE.projectSlide}-${project.id}`}
    >
      <div
        className="img-wrap"
        style={{ backgroundImage: `url(${project.img})` }}
        aria-hidden="true"
      />
      <div className="meta">
        <div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase opacity-80">
            {String(index + 1).padStart(2, "0")} · {project.live ? "Live" : "In progress"}
          </div>
          <div className="mt-1 font-display font-black text-2xl md:text-3xl leading-tight">
            {project.name}
          </div>
          <div className="mt-1 text-sm text-white/85">{project.tagline}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {project.live && (
            <span className="badge live">Live</span>
          )}
          <span className="text-xs text-white/90 flex items-center gap-1 group-hover:text-[#FFE600] transition-colors">
            View case study
            <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Projects() {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  return (
    <section id="projects" className="relative py-24 md:py-36 bg-[#1A1A1A] text-white overflow-hidden">
      {/* Decorative dot grid */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{
             backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
             backgroundSize: "24px 24px",
           }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-[76px] leading-[0.95]">
              Some sites we've{" "}
              <span className="text-[#FFE600]">built</span>{" "}
              <span className="text-white/60">(and are</span>{" "}
              <span className="text-[#E9298A]">proud of</span>
              <span className="text-white/60">).</span>
            </h2>
          </div>
          <div className="max-w-md">
            <p className="text-white/70 leading-relaxed">
              From Hong Kong to clients around the globe — here's a mix of live websites
              and fresh Emergent projects we've delivered for startups and more complex
              needs.
            </p>
          </div>
        </div>

        {/* Active project title */}
        <div className="mt-10 md:mt-14 font-mono text-xs tracking-[0.22em] uppercase text-white/60">
          Now viewing · <span className="text-[#FFE600]">{PROJECTS[activeIndex]?.name}</span>
        </div>
      </div>

      {/* Coverflow */}
      <div className="relative mt-8 md:mt-10">
        <Swiper
          data-testid={SITE.projectsCarousel}
          onSwiper={(sw) => (swiperRef.current = sw)}
          effect="coverflow"
          grabCursor={true}
          centeredSlides={true}
          slidesPerView="auto"
          keyboard={{ enabled: true }}
          loop={true}
          autoplay={{ delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true }}
          onSlideChange={(sw) => setActiveIndex(sw.realIndex)}
          coverflowEffect={{
            rotate: 28,
            stretch: 0,
            depth: 220,
            modifier: 1,
            slideShadows: false,
          }}
          navigation={{
            nextEl: `[data-testid="${SITE.projectsNext}"]`,
            prevEl: `[data-testid="${SITE.projectsPrev}"]`,
          }}
          pagination={{ clickable: true }}
          modules={[EffectCoverflow, Navigation, Pagination, Autoplay, Keyboard]}
          className="tb-coverflow !py-10 md:!py-14"
        >
          {PROJECTS.map((p, i) => (
            <SwiperSlide key={p.id} className="!w-auto">
              <ProjectCard project={p} index={i} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom prev/next buttons */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid={SITE.projectsPrev}
              className="w-12 h-12 rounded-full border border-white/30 text-white bg-white/10 hover:bg-white hover:text-[#1A1A1A] transition-colors flex items-center justify-center"
              aria-label="Previous project"
            >
              ←
            </button>
            <button
              data-testid={SITE.projectsNext}
              className="w-12 h-12 rounded-full border border-white/30 text-white bg-white/10 hover:bg-white hover:text-[#1A1A1A] transition-colors flex items-center justify-center"
              aria-label="Next project"
            >
              →
            </button>
          </div>
          <div className="font-mono text-xs text-white/50 tabular-nums">
            {String(activeIndex + 1).padStart(2, "0")} / {String(PROJECTS.length).padStart(2, "0")}
          </div>
        </div>
      </div>

      {/* Standard project grid (small logos as complementary strip) */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/50 mb-6">
          [ Also seen with ]
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
        >
          {PROJECTS.map((p) => (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              data-testid={`project-grid-${p.id}`}
              className="border border-white/15 rounded-2xl px-4 py-4 text-sm hover:bg-white/5 hover:border-[#FFE600]/40 transition-colors group flex items-center justify-between gap-2"
            >
              <div>
                <div className="font-display font-bold">{p.name}</div>
                <div className="text-white/50 text-xs mt-1">{p.live ? "Live" : "New"}</div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#FFE600] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
